import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Field, PanelShell, TextInput } from "@/components/admin/ui";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("zh-CN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export const EnquiriesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get("/enquiries")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`确定删除「${name}」的咨询？`)) return;
    try {
      await api.delete(`/enquiries/${id}`);
      toast.success("已删除");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <p className="font-mono2 text-xs text-[#a1a1aa] tracking-[0.3em]">LOADING…</p>;
  if (!items.length)
    return (
      <div className="border border-dashed border-white/15 py-20 text-center">
        <p className="font-zh text-[#a1a1aa]">暂无商业咨询 — 官网表单提交后会出现在这里</p>
      </div>
    );

  return (
    <PanelShell title={`咨询联络 (${items.length})`}>
      <div className="space-y-3" data-testid="admin-enquiries-list">
        {items.map((q) => (
          <div key={q.id} className="border border-white/10 bg-[#0a0a0a] p-4 md:p-5" data-testid={`enquiry-row-${q.id}`}>
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-zh font-bold text-white">{q.name}</span>
                  <span className={`font-mono2 text-[9px] px-1.5 py-0.5 ${q.status === "new" ? "bg-[#ffde00] text-black" : "bg-white/10 text-[#a1a1aa]"}`}>
                    {q.status === "new" ? "新咨询" : "已联系"}
                  </span>
                  <span className="font-mono2 text-[10px] text-[#52525b]">{fmtDate(q.created_at)}</span>
                </div>
                <div className="font-mono2 text-xs text-[#a1a1aa] mt-2 space-x-4">
                  <a href={`mailto:${q.email}`} className="hover:text-[#ffde00]">
                    {q.email}
                  </a>
                  {q.phone && <a href={`tel:${q.phone}`} className="hover:text-[#ffde00]">{q.phone}</a>}
                  {q.company && <span>{q.company}</span>}
                </div>
                <div className="font-zh text-xs text-[#a1a1aa] mt-2">
                  {q.event_type && <span className="text-[#ffde00] mr-3">{q.event_type}</span>}
                  {q.budget && <span>预算：{q.budget}</span>}
                </div>
                {q.message && <p className="font-zh text-sm text-[#d4d4d8] mt-3 leading-relaxed">{q.message}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                {q.status === "new" ? (
                  <button onClick={() => setStatus(q.id, "contacted")} className="btn-yellow !py-1.5 !px-3 !text-[10px]" data-testid={`enquiry-contacted-${q.id}`}>
                    标记已联系
                  </button>
                ) : (
                  <button onClick={() => setStatus(q.id, "new")} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`enquiry-reopen-${q.id}`}>
                    重新打开
                  </button>
                )}
                <button onClick={() => remove(q.id, q.name)} className="btn-ghost !py-1.5 !px-3 !text-[10px] hover:!border-red-400 hover:!text-red-400" data-testid={`enquiry-delete-${q.id}`}>
                  删除
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </PanelShell>
  );
};

const SETTING_FIELDS = [
  ["email_eileen", "Eileen 邮箱"],
  ["instagram", "Instagram 账号"],
  ["instagram_url", "Instagram 链接"],
  ["facebook", "Facebook 名称"],
  ["facebook_url", "Facebook 链接"],
  ["tiktok", "TikTok 账号"],
  ["tiktok_url", "TikTok 链接"],
  ["youtube", "YouTube 账号"],
  ["youtube_url", "YouTube 链接"],
  ["xiaohongshu", "小红书账号"],
  ["xiaohongshu_url", "小红书链接"],
  ["weibo", "微博账号"],
  ["weibo_url", "微博链接"],
];

export const SettingsPanel = () => {
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setData(r.data || {}))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", data);
      toast.success("联系信息已更新，官网将立即生效");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelShell title="联系设置">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        className="space-y-4 max-w-xl"
        data-testid="admin-settings-form"
      >
        {SETTING_FIELDS.map(([key, label]) => (
          <Field key={key} label={label}>
            <TextInput value={data[key] || ""} onChange={(e) => setData({ ...data, [key]: e.target.value })} data-testid={`admin-setting-${key}`} />
          </Field>
        ))}
        <button type="submit" disabled={busy} className="btn-yellow disabled:opacity-50" data-testid="admin-settings-save">
          {busy ? "保存中…" : "保存设置"}
          <ArrowUpRight size={14} />
        </button>
      </form>
    </PanelShell>
  );
};
